import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { monitoringWebSocket } from '../services/websocketService';
import { API_BASE_URL } from '../config';
import { eventService } from '../services/eventService';
import {
  enrichEventsWithCctvLocations,
  enrichEventsWithPoolNames,
  mapWebSocketPayloadToUi,
  type CctvLocationMap,
  type PoolNameMap,
} from '../services/eventMappers';
import { useAuth } from './AuthContext';
import { getStoredPoolSiteToken } from '../utils/poolSiteAuth';

// 이벤트 타입 정의
export interface Event {
  id: string;
  ack_id?: string | number;
  client_event_id?: string | null;
  type: 'danger' | 'warning' | 'info';
  title: string;
  description: string;
  poolId: string;
  poolName: string;
  location: string;
  timestamp: Date;
  imageUrl?: string;
  status: 'new' | 'processing' | 'resolved';
  isRead: boolean; // 이벤트가 읽혔는지 여부
  personId?: string;
  personName?: string;
  wearableId?: string;
  metadata?: Record<string, any>; // 추가 데이터를 위한 필드
}

// 이벤트 필터 타입 정의
export interface EventFilter {
  poolId?: string;
  types?: Array<'danger' | 'warning' | 'info'>;
  status?: Array<'new' | 'processing' | 'resolved'>;
  startTime?: Date;
  endTime?: Date;
  isRead?: boolean;
  personId?: string;
  searchText?: string;
}

// 이벤트 구독자 타입 정의
type EventSubscriber = (event: Event) => void;

// EventContext 인터페이스 정의
interface EventContextType {
  events: Event[];
  filteredEvents: Event[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  updateEventStatus: (eventId: string, status: 'new' | 'processing' | 'resolved') => void;
  /** 캡처 bbox 저장 후 목록 썸네일 URL 갱신 (cache-bust 포함 가능) */
  patchEventImageUrl: (eventId: string, imageUrl: string, eventDbId?: number | string) => void;
  /** 이미지·메시지·제목 등 목록 표시 필드 부분 갱신 */
  patchEventDisplay: (
    eventId: string,
    patch: { imageUrl?: string; description?: string; title?: string },
    eventDbId?: number | string
  ) => void;
  acknowledgeEvent: (eventId: string, falsePositive?: boolean, note?: string) => Promise<void>;
  markAsRead: (eventId: string) => void;
  markAllAsRead: () => void;
  fetchEvents: () => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  bulkDeleteEvents: (eventUiIds: string[], poolId?: string) => Promise<number>;
  deleteAllEvents: (poolId?: string) => Promise<number>;
  clearEvents: () => void;
  subscribeToEvents: (subscriber: EventSubscriber) => () => void;
  setFilter: (filter: EventFilter) => void;
  clearFilter: () => void;
  currentFilter: EventFilter;
}

// 기본값 생성
const defaultEventContext: EventContextType = {
  events: [],
  filteredEvents: [],
  unreadCount: 0,
  loading: false,
  error: null,
  updateEventStatus: () => {},
  patchEventImageUrl: () => {},
  patchEventDisplay: () => {},
  acknowledgeEvent: async () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  fetchEvents: async () => {},
  deleteEvent: async () => {},
  bulkDeleteEvents: async () => 0,
  deleteAllEvents: async () => 0,
  clearEvents: () => {},
  subscribeToEvents: () => () => {},
  setFilter: () => {},
  clearFilter: () => {},
  currentFilter: {},
};

// Context 생성
const EventContext = createContext<EventContextType>(defaultEventContext);

// EventContext 훅
export const useEventContext = () => useContext(EventContext);

// EventContext Provider 컴포넌트
interface EventProviderProps {
  children: ReactNode;
  apiUrl?: string;
  maxEventHistory?: number; // 최대 이벤트 히스토리 개수
}

export const EventProvider: React.FC<EventProviderProps> = ({ 
  children, 
  apiUrl = `${API_BASE_URL}/events`,
  maxEventHistory = 100 // 기본값 100개
}) => {
  const { authState } = useAuth();
  const sessionReady =
    !authState.loading &&
    typeof window !== 'undefined' &&
    ((authState.isAuthenticated &&
      authState.isAdminPasswordVerified &&
      Boolean(localStorage.getItem('token'))) ||
      Boolean(getStoredPoolSiteToken()));

  const [events, setEvents] = useState<Event[]>([]);
  const [filter, setFilter] = useState<EventFilter>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const poolNamesRef = useRef<PoolNameMap>({});
  const cctvLocationsRef = useRef<CctvLocationMap>({});

  // 이벤트 구독자 관리
  const [subscribers, setSubscribers] = useState<Set<EventSubscriber>>(new Set());
  
  // 이벤트 구독 함수
  const subscribeToEvents = useCallback((subscriber: EventSubscriber) => {
    setSubscribers(prev => {
      const newSubscribers = new Set(prev);
      newSubscribers.add(subscriber);
      return newSubscribers;
    });
    
    // 구독 해제 함수 반환
    return () => {
      setSubscribers(prev => {
        const newSubscribers = new Set(prev);
        newSubscribers.delete(subscriber);
        return newSubscribers;
      });
    };
  }, []);
  
  // 필터링된 이벤트
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // 풀 ID 필터링
      if (filter.poolId && String(event.poolId) !== String(filter.poolId)) {
        return false;
      }
      
      // 이벤트 타입 필터링
      if (filter.types && filter.types.length > 0 && !filter.types.includes(event.type)) {
        return false;
      }
      
      // 상태 필터링
      if (filter.status && filter.status.length > 0 && !filter.status.includes(event.status)) {
        return false;
      }
      
      // 시간 범위 필터링
      if (filter.startTime && new Date(event.timestamp) < filter.startTime) {
        return false;
      }
      
      if (filter.endTime && new Date(event.timestamp) > filter.endTime) {
        return false;
      }
      
      // 읽음 상태 필터링
      if (filter.isRead !== undefined && event.isRead !== filter.isRead) {
        return false;
      }
      
      // 사람 ID 필터링
      if (filter.personId && event.personId !== filter.personId) {
        return false;
      }
      
      // 검색어 필터링
      if (filter.searchText) {
        const searchTextLower = filter.searchText.toLowerCase();
        const matchesSearch = 
          event.title.toLowerCase().includes(searchTextLower) ||
          event.description.toLowerCase().includes(searchTextLower) ||
          event.poolName.toLowerCase().includes(searchTextLower) ||
          event.location.toLowerCase().includes(searchTextLower) ||
          (event.personName && event.personName.toLowerCase().includes(searchTextLower));
        
        if (!matchesSearch) {
          return false;
        }
      }
      
      return true;
    });
  }, [events, filter]);
  
  // 읽지 않은 이벤트 개수
  const unreadCount = useMemo(() => {
    return events.filter(event => !event.isRead).length;
  }, [events]);
  
  const loadDemoEvents = (): Event[] => [
        {
          id: '1',
          type: 'danger',
          title: '익수 위험',
          description: '시설 가장자리에서 익수 위험 감지',
          poolId: 'pool1',
          poolName: '메인 시설',
          location: '레인 3',
          timestamp: new Date(),
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+1',
          status: 'new',
          isRead: false,
        },
        {
          id: '2',
          type: 'warning',
          title: '구역 이탈',
          description: '안전 구역 이탈 감지',
          poolId: 'pool1',
          poolName: '메인 시설',
          location: '레인 5',
          timestamp: new Date(Date.now() - 5 * 60000), // 5분 전
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+2',
          status: 'processing',
          isRead: true,
          personId: 'person1',
          personName: '김철수',
          wearableId: 'w123',
        },
        {
          id: '3',
          type: 'info',
          title: '장시간 체류',
          description: '동일 구역 20분 이상 체류',
          poolId: 'pool2',
          poolName: '어린이 시설',
          location: '중앙',
          timestamp: new Date(Date.now() - 15 * 60000), // 15분 전
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+3',
          status: 'new',
          isRead: false,
          personId: 'person2',
          personName: '이영희',
          wearableId: 'w456',
        },
        {
          id: '4',
          type: 'danger',
          title: '심박수 이상',
          description: '비정상 심박수 감지 (140 BPM 이상)',
          poolId: 'pool3',
          poolName: '실내 시설',
          location: '레인 2',
          timestamp: new Date(Date.now() - 2 * 60000), // 2분 전
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+4',
          status: 'new',
          isRead: false,
          personId: 'person3',
          personName: '박민수',
          wearableId: 'w789',
        },
        {
          id: '5',
          type: 'warning',
          title: '웨어러블 연결 끊김',
          description: '웨어러블 기기 연결 손실',
          poolId: 'pool1',
          poolName: '메인 시설',
          location: '레인 7',
          timestamp: new Date(Date.now() - 7 * 60000), // 7분 전
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+5',
          status: 'resolved',
          isRead: true,
          personId: 'person4',
          personName: '최지우',
          wearableId: 'w101',
        },
        {
          id: '6',
          type: 'danger',
          title: '움직임 없음',
          description: '장시간 움직임 없음 감지',
          poolId: 'pool2',
          poolName: '어린이 시설',
          location: '얕은 지역',
          timestamp: new Date(),
          imageUrl: 'https://via.placeholder.com/300x200?text=Event+6',
          status: 'new',
          isRead: false,
          personId: 'person5',
          personName: '정소연',
          wearableId: 'w102',
        },
      ];

  // 이벤트 목록 가져오기 (API만 사용, 목업 폴백 없음)
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const poolIdParam = filter.poolId ? parseInt(String(filter.poolId), 10) : undefined;
      const poolId =
        poolIdParam != null && !Number.isNaN(poolIdParam) ? poolIdParam : undefined;
      const names = poolNamesRef.current;
      const cctvLocs = cctvLocationsRef.current;
      const list = await eventService.getEventsForUi(poolId, maxEventHistory, names, cctvLocs);
      const enriched = enrichEventsWithCctvLocations(
        enrichEventsWithPoolNames(list, names),
        cctvLocs
      );
      // 증거 영상 갱신으로 캡처 URL이 잠깐 빠질 수 있음 — 기존 imageUrl 유지
      setEvents((prev) => {
        const prevByKey = new Map<string, Event>();
        for (const e of prev) {
          prevByKey.set(e.id, e);
          if (e.ack_id != null) prevByKey.set(`ack:${e.ack_id}`, e);
          if (e.metadata?.event_db_id != null) {
            prevByKey.set(`db:${e.metadata.event_db_id}`, e);
          }
        }
        return enriched.map((e) => {
          const old =
            prevByKey.get(e.id) ||
            (e.ack_id != null ? prevByKey.get(`ack:${e.ack_id}`) : undefined) ||
            (e.metadata?.event_db_id != null
              ? prevByKey.get(`db:${e.metadata.event_db_id}`)
              : undefined);
          if (old?.imageUrl && !e.imageUrl) {
            return { ...e, imageUrl: old.imageUrl };
          }
          return e;
        });
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('이벤트를 가져오는 중 오류 발생');
      }
      // 일시 오류로 목록을 비우지 않음 — 기존 내역 유지
    } finally {
      setLoading(false);
    }
  }, [filter.poolId, maxEventHistory]);

  const acknowledgeEvent = useCallback(
    async (eventId: string, falsePositive = false, note?: string) => {
      const ev = events.find((e) => e.id === eventId);
      const ackTarget = ev
        ? eventService.resolveAckId(ev as Parameters<typeof eventService.resolveAckId>[0])
        : eventId;
      await eventService.ackEvent(ackTarget, falsePositive, note);
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventId ? { ...event, status: 'resolved' as const, isRead: true } : event
        )
      );
    },
    [events]
  );

  // 이벤트 상태 업데이트
  const updateEventStatus = useCallback(
    (eventId: string, status: 'new' | 'processing' | 'resolved') => {
      setEvents((prevEvents) =>
        prevEvents.map((event) => (event.id === eventId ? { ...event, status } : event))
      );
      if (status === 'resolved') {
        eventService.ackEvent(eventId, false).catch((err) => {
          console.error('이벤트 ACK 실패:', err);
          setError('이벤트 확인 API 호출에 실패');
        });
      }
    },
    []
  );

  const patchEventImageUrl = useCallback(
    (eventId: string, imageUrl: string, eventDbId?: number | string) => {
      const dbStr =
        eventDbId != null && String(eventDbId).trim() !== ''
          ? String(eventDbId)
          : '';
      setEvents((prev) =>
        prev.map((e) => {
          const metaDb = e.metadata?.event_db_id != null ? String(e.metadata.event_db_id) : '';
          const ack = e.ack_id != null ? String(e.ack_id) : '';
          const match =
            e.id === eventId ||
            (dbStr !== '' && (metaDb === dbStr || ack === dbStr));
          return match ? { ...e, imageUrl } : e;
        })
      );
    },
    []
  );

  const patchEventDisplay = useCallback(
    (
      eventId: string,
      patch: { imageUrl?: string; description?: string; title?: string },
      eventDbId?: number | string
    ) => {
      const dbStr =
        eventDbId != null && String(eventDbId).trim() !== ''
          ? String(eventDbId)
          : '';
      setEvents((prev) =>
        prev.map((e) => {
          const metaDb = e.metadata?.event_db_id != null ? String(e.metadata.event_db_id) : '';
          const ack = e.ack_id != null ? String(e.ack_id) : '';
          const match =
            e.id === eventId ||
            (dbStr !== '' && (metaDb === dbStr || ack === dbStr));
          if (!match) return e;
          return {
            ...e,
            ...(patch.imageUrl != null ? { imageUrl: patch.imageUrl } : {}),
            ...(patch.description != null ? { description: patch.description } : {}),
            ...(patch.title != null ? { title: patch.title } : {}),
          };
        })
      );
    },
    []
  );
  
  // 이벤트를 읽음으로 표시
  const markAsRead = useCallback((eventId: string) => {
    setEvents(prevEvents => 
      prevEvents.map(event => 
        event.id === eventId ? { ...event, isRead: true } : event
      )
    );
    
    // 실제 API 호출
    // axios.put(`${apiUrl}/${eventId}/read`, { isRead: true })
    //   .catch(err => {
    //     console.error('이벤트 읽음 상태 업데이트 실패:', err);
    //     setError('이벤트 읽음 상태를 업데이트하는 중 오류가 발생했습니다.');
    //   });
  }, []);
  
  // 모든 이벤트를 읽음으로 표시
  const markAllAsRead = useCallback(() => {
    setEvents(prevEvents => 
      prevEvents.map(event => ({ ...event, isRead: true }))
    );
    
    // 실제 API 호출
    // axios.put(`${apiUrl}/read-all`)
    //   .catch(err => {
    //     console.error('모든 이벤트 읽음 상태 업데이트 실패:', err);
    //     setError('모든 이벤트 읽음 상태를 업데이트하는 중 오류가 발생했습니다.');
    //   });
  }, []);
  
  // 이벤트 삭제
  const deleteEvent = useCallback(async (eventId: string) => {
    const target = events.find((event) => event.id === eventId);
    const eventDbId =
      (target?.metadata?.event_db_id as number | string | undefined) ??
      target?.ack_id ??
      eventId;
    try {
      await eventService.deleteEvent(eventDbId);
      setEvents((prevEvents) => prevEvents.filter((event) => event.id !== eventId));
    } catch (err) {
      console.error('이벤트 삭제 실패:', err);
      setError('이벤트를 삭제하는 중 오류 발생');
      throw err;
    }
  }, [events]);
  
  const resolveApiEventId = useCallback((event: Event): string => {
    const fromMeta = event.metadata?.event_db_id;
    if (fromMeta != null && Number.isFinite(Number(fromMeta))) return String(fromMeta);
    if (event.ack_id != null) return String(event.ack_id);
    return String(event.id);
  }, []);

  const bulkDeleteEvents = useCallback(
    async (eventUiIds: string[], scopePoolId?: string) => {
      const idSet = new Set(eventUiIds);
      const targets = events.filter((e) => idSet.has(e.id));
      const eventIds = targets.map((e) => resolveApiEventId(e));
      const poolNum =
        scopePoolId != null && scopePoolId !== '' ? Number(scopePoolId) : undefined;
      try {
        const deleted = await eventService.bulkDelete({
          eventIds,
          poolId: Number.isFinite(poolNum) ? poolNum : undefined,
        });
        setEvents((prev) => prev.filter((e) => !idSet.has(e.id)));
        return deleted;
      } catch (err) {
        console.error('이벤트 일괄 삭제 실패:', err);
        setError('이벤트를 삭제하는 중 오류 발생');
        throw err;
      }
    },
    [events, resolveApiEventId],
  );

  const deleteAllEvents = useCallback(
    async (scopePoolId?: string) => {
      const poolNum =
        scopePoolId != null && scopePoolId !== '' ? Number(scopePoolId) : undefined;
      try {
        const deleted = await eventService.bulkDelete({
          all: true,
          poolId: Number.isFinite(poolNum) ? poolNum : undefined,
        });
        if (Number.isFinite(poolNum)) {
          setEvents((prev) => prev.filter((e) => e.poolId !== String(poolNum)));
        } else {
          setEvents([]);
        }
        return deleted;
      } catch (err) {
        console.error('이벤트 전체 삭제 실패:', err);
        setError('이벤트를 삭제하는 중 오류 발생');
        throw err;
      }
    },
    [],
  );
  
  // 모든 이벤트 삭제
  const clearEvents = useCallback(() => {
    setEvents([]);
    
    // 실제 API 호출
    // axios.delete(`${apiUrl}/all`)
    //   .catch(err => {
    //     console.error('모든 이벤트 삭제 실패:', err);
    //     setError('모든 이벤트를 삭제하는 중 오류가 발생했습니다.');
    //   });
  }, []);
  
  // 필터 설정 (부분 갱신 — undefined 키는 제거)
  const setFilterHandler = useCallback((newFilter: EventFilter) => {
    setFilter((prev) => {
      const next: EventFilter = { ...prev, ...newFilter };
      (Object.keys(newFilter) as (keyof EventFilter)[]).forEach((key) => {
        if (newFilter[key] === undefined) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);
  
  // 필터 초기화
  const clearFilter = useCallback(() => {
    setFilter({});
  }, []);
  
  // 새 이벤트 처리
  const handleNewEvent = useCallback((event: Event) => {
    // 중복 방지 (id / ack_id / event_db_id)
    setEvents(prevEvents => {
      const incomingDbId =
        event.ack_id != null
          ? String(event.ack_id)
          : event.metadata?.event_db_id != null
            ? String(event.metadata.event_db_id)
            : null;
      const isDup = prevEvents.some((e) => {
        if (e.id === event.id) return true;
        if (incomingDbId && e.ack_id != null && String(e.ack_id) === incomingDbId) {
          return true;
        }
        if (
          incomingDbId &&
          e.metadata?.event_db_id != null &&
          String(e.metadata.event_db_id) === incomingDbId
        ) {
          return true;
        }
        return false;
      });
      if (isDup) {
        // 동일 이벤트면 최신 필드로 갱신
        return prevEvents
          .map((e) => {
            const sameId = e.id === event.id;
            const sameAck =
              incomingDbId != null &&
              ((e.ack_id != null && String(e.ack_id) === incomingDbId) ||
                (e.metadata?.event_db_id != null &&
                  String(e.metadata.event_db_id) === incomingDbId));
            return sameId || sameAck
              ? {
                  ...e,
                  ...event,
                  // 이후 WS/갱신에 캡처 URL이 없어도 이미 있던 썸네일 유지
                  imageUrl: event.imageUrl || e.imageUrl,
                  isRead: e.isRead || event.isRead,
                }
              : e;
          })
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }
      
      // 기본값으로 읽지 않음 상태 설정
      const eventWithReadStatus = { ...event, isRead: event.isRead ?? false };
      
      // 최신 이벤트가 앞에 오도록 정렬
      const updatedEvents = [eventWithReadStatus, ...prevEvents]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      // 최대 이벤트 개수로 제한
      return updatedEvents.slice(0, maxEventHistory);
    });
    
    // 모든 구독자에게 알림
    subscribers.forEach(subscriber => {
      try {
        subscriber(event);
      } catch (error) {
        console.error('이벤트 구독자에게 알림 중 오류:', error);
      }
    });
  }, [subscribers, maxEventHistory]);

  useEffect(() => {
    if (!sessionReady) return;
    let cancelled = false;

    (async () => {
      try {
        const [poolMap, cctvMap] = await Promise.all([
          eventService.fetchPoolNameMap(),
          eventService.fetchCctvLocationMap(),
        ]);
        if (cancelled) return;
        poolNamesRef.current = poolMap;
        cctvLocationsRef.current = cctvMap;
        // 시설 맵을 채운 뒤 목록 조회 — 「시설 #1」 레이스 방지
        await fetchEvents();
        if (cancelled) return;
        if (Object.keys(poolMap).length > 0 || Object.keys(cctvMap).length > 0) {
          setEvents((prev) =>
            enrichEventsWithCctvLocations(enrichEventsWithPoolNames(prev, poolMap), cctvMap)
          );
        }
      } catch (err) {
        console.error('이벤트/시설명 초기 로드 실패:', err);
        if (!cancelled) {
          await fetchEvents();
        }
      }
    })();

    const handleEventMessage = (data: unknown) => {
      // 해결 통지는 새 행 추가 대신 기존 이벤트 상태만 갱신
      if (
        data &&
        typeof data === 'object' &&
        String((data as { type?: string }).type ?? '') === 'event_resolved'
      ) {
        const raw = data as { event_id?: string | number; ack_id?: string | number };
        const resolvedKey = String(raw.event_id ?? raw.ack_id ?? '');
        if (resolvedKey) {
          setEvents((prev) =>
            prev.map((e) => {
              const same =
                e.id === resolvedKey ||
                String(e.ack_id ?? '') === resolvedKey ||
                String(e.metadata?.event_db_id ?? '') === resolvedKey;
              return same ? { ...e, status: 'resolved' as const, isRead: true } : e;
            })
          );
        }
        return;
      }
      const mapped = mapWebSocketPayloadToUi(data, poolNamesRef.current);
      if (mapped) {
        const enriched = enrichEventsWithCctvLocations([mapped], cctvLocationsRef.current);
        handleNewEvent(enriched[0] ?? mapped);
      }
    };

    monitoringWebSocket.subscribe('events', handleEventMessage);

    return () => {
      cancelled = true;
      monitoringWebSocket.unsubscribe('events', handleEventMessage);
    };
  }, [sessionReady, fetchEvents, handleNewEvent]);
  
  return (
    <EventContext.Provider value={{ 
      events, 
      filteredEvents,
      unreadCount,
      loading, 
      error, 
      updateEventStatus,
      patchEventImageUrl,
      patchEventDisplay,
      acknowledgeEvent,
      markAsRead,
      markAllAsRead,
      fetchEvents, 
      deleteEvent,
      bulkDeleteEvents,
      deleteAllEvents,
      clearEvents,
      subscribeToEvents,
      setFilter: setFilterHandler,
      clearFilter,
      currentFilter: filter
    }}>
      {children}
    </EventContext.Provider>
  );
};

export default EventContext; 